import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { closeNoteModal, showNoteModal } from 'store/noteModal/noteModalSlice';
import {
  closeAiScribeDrawer,
  isAiScribeRecordingStatus,
  minimizeAiScribeDrawer,
  pauseAiScribe,
  resetDuration,
  resumeAiScribe,
  setDuration,
  setError,
  setStatus
} from 'store/aiScribe/aiScribeSlice';
import {
  initializeRecordingSession,
  startRecordingTranscription
} from 'store/aiScribe/aiScribeActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { notifyUserSuccess, notifyUserWarning } from 'utils/notifications';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import { useAiScribeChunkUploader } from './useAiScribeChunkUploader';
import { useAudioRecorderController } from './useAudioRecorderController';
import { useAudioWaveform } from './useAudioWaveform';
import { useRecordingTimer } from './useRecordingTimer';
import { useScreenWakeLock } from './useScreenWakeLock';

const MAX_REACQUIRE_ATTEMPTS = 3;
const PARTIAL_AUDIO_WARNING =
  'Some audio could not be uploaded because of connection issues. The generated note may be incomplete.';
const INTERRUPTED_MESSAGE = 'Recording was interrupted. Tap Resume to continue.';
const MICROPHONE_PERMISSION_MESSAGE =
  'Microphone access is blocked. Please allow microphone access in your browser settings and try again.';

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getReacquireDelayMs = (attempt: number) => {
  const baseDelayMs = 500;
  const maxDelayMs = 4000;
  const jitterMs = Math.floor(Math.random() * 250);

  return Math.min(baseDelayMs * 2 ** attempt + jitterMs, maxDelayMs);
};

const getErrorField = (error: unknown, field: 'name' | 'message') => {
  if (error instanceof Error) return error[field];

  if (typeof error === 'object' && error != null && field in error) {
    const value = (error as Record<typeof field, unknown>)[field];
    return typeof value === 'string' ? value : '';
  }

  return typeof error === 'string' && field === 'message' ? error : '';
};

const isMicrophonePermissionError = (error: unknown) => {
  const errorName = getErrorField(error, 'name');
  const errorMessage = getErrorField(error, 'message').toLowerCase();

  return (
    errorName === 'NotAllowedError' ||
    errorName === 'SecurityError' ||
    errorMessage.includes('notallowederror') ||
    errorMessage.includes('not allowed by the user agent') ||
    errorMessage.includes('permission denied')
  );
};

const getMicrophoneErrorMessage = (error: unknown, fallbackMessage: string) => {
  return isMicrophonePermissionError(error) ? MICROPHONE_PERMISSION_MESSAGE : fallbackMessage;
};

export const useAiScribe = () => {
  const dispatch = useAppDispatch();
  const {
    state,
    clientName,
    prependedClientId,
    clientId,
    status,
    isPaused,
    duration,
    error: aiScribeError,
    fileUuid,
    lastOrdinalNumber,
    isResumedFromExternalSource,
    note,
    initializing
  } = useAppSelector((s) => s.aiScribe);
  const { enableMpiUpdate } = useFlags();
  const { isTablet } = useResponsive();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeRecordingSessionId, setActiveRecordingSessionId] = useState<string | null>(fileUuid);
  const handleInterruptedRef = useRef<() => Promise<void>>(async () => undefined);
  const handleDurationChange = useCallback(
    (nextDuration: number) => dispatch(setDuration(nextDuration)),
    [dispatch]
  );
  const handleResetDuration = useCallback(() => dispatch(resetDuration()), [dispatch]);

  const timer = useRecordingTimer({
    durationSeconds: duration,
    onDurationChange: handleDurationChange,
    onResetDuration: handleResetDuration
  });

  const isRecordingActive = isAiScribeRecordingStatus(status);
  useScreenWakeLock(isRecordingActive);

  const waveform = useAudioWaveform({
    drawerState: state,
    isRecordingActive,
    isPaused
  });

  const uploader = useAiScribeChunkUploader({ clientId });
  const handleRecorderInterrupted = useCallback(() => handleInterruptedRef.current(), []);
  const handleRecorderError = useCallback(
    (error: unknown) => {
      dispatch(setStatus('failed'));
      dispatch(setError('System error. Please try again'));
      console.error('AI Scribe recorder error:', error);
    },
    [dispatch]
  );

  const recorder = useAudioRecorderController({
    onInterrupted: handleRecorderInterrupted,
    onError: handleRecorderError,
    onStreamReady: waveform.setup,
    getChunkDurationSeconds: timer.getChunkDurationSeconds
  });

  const startTranscription = useCallback(
    async (recordingSessionId: string) => {
      try {
        dispatch(setStatus('transcribing'));
        await dispatch(startRecordingTranscription({ clientId, recordingSessionId })).unwrap();
        const channel = new BroadcastChannel('note-update');
        channel.postMessage({ message: 'EDIT_NOTE' });
        if (enableMpiUpdate && !isTablet) {
          notifyUserSuccess('Your draft note was created');
        }
        dispatch(setStatus('transcribed'));
      } catch (error) {
        dispatch(setStatus('failed'));
        console.error('Error starting transcription:', error);
      }
    },
    [clientId, dispatch, enableMpiUpdate, isTablet]
  );

  const startRecorderForCurrentSession = useCallback(
    async (recordingSessionId: string, { resetSessionDuration = false } = {}) => {
      await recorder.start({
        onChunk: (chunk) => uploader.uploadChunk({ recordingSessionId, chunk })
      });
      timer.start({ resetDuration: resetSessionDuration });
      dispatch(resumeAiScribe());
      dispatch(setStatus('recording'));
    },
    [dispatch, recorder, timer, uploader]
  );

  handleInterruptedRef.current = async () => {
    timer.stop({ commitElapsed: true });
    waveform.cleanup();
    dispatch(pauseAiScribe());
    dispatch(setStatus('interrupted'));
    dispatch(setError(INTERRUPTED_MESSAGE));

    dispatch(setStatus('reacquiring_microphone'));

    for (let attempt = 0; attempt < MAX_REACQUIRE_ATTEMPTS; attempt += 1) {
      try {
        await sleep(getReacquireDelayMs(attempt));

        recorder.cleanup();
        const recordingSessionId = activeRecordingSessionId ?? fileUuid;
        if (!recordingSessionId) return;

        await startRecorderForCurrentSession(recordingSessionId);
        dispatch(setError(null));
        return;
      } catch (error) {
        if (attempt === MAX_REACQUIRE_ATTEMPTS - 1) {
          const errorMessage = getMicrophoneErrorMessage(error, INTERRUPTED_MESSAGE);
          dispatch(setStatus('interrupted'));
          dispatch(setError(errorMessage));
          console.error('AI Scribe microphone reacquire failed:', error);
        }
      }
    }
  };

  const startRecording = useCallback(
    async ({ noteType }: { noteType: string }) => {
      try {
        dispatch(setStatus('initializing'));
        await recorder.requestStream();

        const data = await dispatch(
          initializeRecordingSession({
            clientId,
            noteType,
            note
          })
        ).unwrap();

        const recordingSessionId = data.fileUuid;
        if (!recordingSessionId) {
          dispatch(setStatus('idle'));
          dispatch(showNoteModal());
          return;
        }

        setActiveRecordingSessionId(recordingSessionId);
        uploader.reset(0);

        await startRecorderForCurrentSession(recordingSessionId, { resetSessionDuration: true });
        dispatch(closeNoteModal());
      } catch (error) {
        const errorMessage = getMicrophoneErrorMessage(error, 'System error. Please try again');
        timer.reset();
        recorder.cleanup();
        dispatch(setStatus('idle'));
        dispatch(setError(errorMessage));
        dispatch(showNoteModal());
        console.error('Error accessing microphone:', error);
      }
    },
    [clientId, dispatch, note, recorder, startRecorderForCurrentSession, timer, uploader]
  );

  const stopRecording = useCallback(async () => {
    const recordingSessionId = activeRecordingSessionId ?? fileUuid;
    if (!recordingSessionId) {
      dispatch(setError('System error. Please try again'));
      return;
    }

    setIsSubmitting(true);
    dispatch(setStatus('finishing'));
    timer.stop({ commitElapsed: true });

    try {
      if (recorder.hasRecorder()) {
        await recorder.submit();
      }

      await uploader.waitForPendingUploads();
      waveform.cleanup();
      recorder.cleanup();

      if (uploader.hasUploadFailure()) {
        notifyUserWarning(PARTIAL_AUDIO_WARNING);
      }

      await startTranscription(recordingSessionId);
    } catch (error) {
      dispatch(setStatus('failed'));
      console.error('Error finishing AI Scribe recording:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    activeRecordingSessionId,
    dispatch,
    fileUuid,
    recorder,
    startTranscription,
    timer,
    uploader,
    waveform
  ]);

  const pauseRecording = useCallback(() => {
    if (!recorder.hasRecorder() || recorder.isPaused()) return;

    recorder.pause();
    timer.stop({ commitElapsed: true });
    dispatch(pauseAiScribe());
    dispatch(setStatus('paused'));
  }, [dispatch, recorder, timer]);

  const resumeRecording = useCallback(async () => {
    const recordingSessionId = activeRecordingSessionId ?? fileUuid;

    if (!recordingSessionId) {
      dispatch(setError('System error. Please try again'));
      return;
    }

    try {
      if (isResumedFromExternalSource && !recorder.hasRecorder()) {
        setActiveRecordingSessionId(recordingSessionId);
        uploader.reset(lastOrdinalNumber ?? 0);
        await startRecorderForCurrentSession(recordingSessionId);
        return;
      }

      if (status === 'interrupted' || !recorder.hasRecorder()) {
        setActiveRecordingSessionId(recordingSessionId);
        uploader.reset(lastOrdinalNumber ?? 0);
        await startRecorderForCurrentSession(recordingSessionId);
        dispatch(setError(null));
        return;
      }

      if (recorder.isPaused()) {
        recorder.resume();
        timer.start();
        dispatch(resumeAiScribe());
        dispatch(setStatus('recording'));
      }
    } catch (error) {
      const errorMessage = getMicrophoneErrorMessage(error, INTERRUPTED_MESSAGE);
      dispatch(setStatus('interrupted'));
      dispatch(setError(errorMessage));
      console.error('Error resuming AI Scribe recording:', error);
    }
  }, [
    activeRecordingSessionId,
    dispatch,
    fileUuid,
    isResumedFromExternalSource,
    lastOrdinalNumber,
    recorder,
    startRecorderForCurrentSession,
    status,
    timer,
    uploader
  ]);

  const handleDrawerClose = useCallback(() => {
    if (status === 'idle' || status === 'transcribed' || status === 'failed') {
      if (status === 'idle') {
        dispatch(showNoteModal());
      }
      dispatch(closeAiScribeDrawer());
    } else {
      dispatch(minimizeAiScribeDrawer());
    }
  }, [dispatch, status]);

  useEffect(() => {
    setActiveRecordingSessionId(fileUuid);
  }, [fileUuid]);

  useEffect(() => {
    return () => {
      timer.stop();
      waveform.cleanup();
      recorder.cleanup();
    };
  }, [recorder, timer, waveform]);

  return {
    initializing,
    state,
    status,
    isPaused,
    duration,
    clientName,
    prependedClientId,
    error: aiScribeError,
    isSubmitting,
    canvasRef: waveform.canvasRef,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    handleDrawerClose
  };
};

const AiScribeContext = createContext<ReturnType<typeof useAiScribe> | null>(null);

export const AiScribeProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useAiScribe();

  return <AiScribeContext.Provider value={value}>{children}</AiScribeContext.Provider>;
};

export const useAiScribeContext = () => {
  const context = useContext(AiScribeContext);
  if (!context) {
    throw new Error('useAiScribe must be used within an AiScribeProvider');
  }
  return context;
};
